import { forwardRef } from 'react';
import { Send } from 'lucide-react';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
}

export const MessageInput = forwardRef<HTMLInputElement, MessageInputProps>(
  function MessageInput({ value, onChange, onSend }, ref) {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    };

    // The composer is the last row of the conversation column, not a fixed
    // overlay. Fixed meant it was positioned against the window rather than the
    // pane: it had to be nudged past the conversation list by hand (`md:ml-96`,
    // which was already wrong at `lg`, where the list is `w-128`), it sat on
    // top of the last message instead of above it, and on iOS — where the
    // keyboard covers the page rather than resizing it — it stayed where the
    // window said it was, which is behind the keys.
    // The inset is on the outer element and the spacing on the inner one, on
    // purpose: both are `padding-bottom`, both are utilities, and Tailwind's
    // wins on source order — put them on one element and the safe-area inset
    // quietly does nothing (the same trap `.top-safe-padding-4` exists for).
    return (
      <div className="flex-none bottom-safe-padding-adaptive touch-none-chrome">
        <div className="p-2 flex items-center gap-2">
          <input
            ref={ref}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={1000}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm border dark:border-neutral-700 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent text-gray-900 dark:text-neutral-100 placeholder-gray-400 dark:placeholder-neutral-500"
          />
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onSend}
            disabled={!value.trim()}
            className="w-12 h-12 rounded-full bg-primary-800 text-white flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed hover:bg-primary-600 transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }
);

